import React, { useState, useEffect, useMemo, useRef } from 'react';
import { P, font, mono } from '../styles/theme';
import { Spinner, Card } from '../components/Shared';
import { CongTyService, KskImportService, KskPrintService } from '../services/api';

/**
 * Trả kết quả KSK & gửi SMS — web hóa frmPrintMany_QueVo:
 *   Lọc theo công ty / số lô / khoảng ngày → danh sách bệnh nhân (trạng thái
 *   đã tạo file / đã gửi SMS) → chọn → "Thực hiện": sinh PDF phiếu, đẩy online
 *   (kcb_thongtin_file + FileApi), gửi SMS VNPT (mật khẩu tra cứu) + log.
 */
export default function AdminPrintKskPage({ onBack }) {
  // Bộ lọc
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [searchCty, setSearchCty] = useState('');
  const [openCty, setOpenCty] = useState(false);
  const [soLoList, setSoLoList] = useState([]);
  const [soLo, setSoLo] = useState('');
  const [tuNgay, setTuNgay] = useState('');
  const [denNgay, setDenNgay] = useState('');
  const ctyRef = useRef(null);

  // Tùy chọn xử lý
  const [guiOnline, setGuiOnline] = useState(true);
  const [guiSms, setGuiSms] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [ngayIn, setNgayIn] = useState(() => new Date().toISOString().slice(0, 10));

  // Data
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    (async () => {
      try { setCompanies(await CongTyService.list() || []); }
      catch { setMessage({ type: 'err', text: 'Không tải được danh mục công ty.' }); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (company?.idKhachHang) {
        try { setSoLoList(await KskImportService.getSoLo(company.idKhachHang) || []); }
        catch { setSoLoList([]); }
      } else setSoLoList([]);
    })();
  }, [company]);

  useEffect(() => {
    const h = (e) => { if (ctyRef.current && !ctyRef.current.contains(e.target)) setOpenCty(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredCty = useMemo(() => {
    const q = searchCty.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c =>
      (c.tenKhachHang || '').toLowerCase().includes(q) ||
      (c.maKhachHang || '').toLowerCase().includes(q));
  }, [companies, searchCty]);

  // ---------- Load danh sách ----------
  const loadDanhSach = async () => {
    if (!company && !soLo.trim()) { setMessage({ type: 'err', text: 'Chọn công ty hoặc nhập số lô.' }); return; }
    setBusy('load'); setMessage(null); setResult(null); setLogs([]);
    try {
      const data = await KskPrintService.danhSach({
        idKhachHang: company?.idKhachHang || 0,
        soLo: soLo.trim(), tuNgay, denNgay,
      });
      setRows(data || []);
      // Mặc định chọn những người CHƯA tạo file
      setChecked(new Set((data || []).map((r, i) => (!r.daTaoFile ? i : null)).filter(i => i !== null)));
      setMessage({ type: 'ok', text: `Đã tải ${data.length} bệnh nhân (${data.filter(r => r.daTaoFile).length} đã có phiếu, ${data.filter(r => r.daGuiSms).length} đã gửi SMS).` });
    } catch (err) {
      setRows([]);
      setMessage({ type: 'err', text: err.response?.data?.message || 'Không tải được danh sách.' });
    } finally { setBusy(''); }
  };

  // ---------- Preview 1 phiếu ----------
  const preview = async (r) => {
    setBusy('preview:' + r.maLuotKham);
    try {
      const url = await KskPrintService.pdfUrl(r.maLuotKham);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      setMessage({ type: 'err', text: 'Không sinh được PDF cho ' + r.maLuotKham });
    } finally { setBusy(''); }
  };

  // ---------- Thực hiện hàng loạt ----------
  const doProcess = async () => {
    const selected = rows.filter((_, i) => checked.has(i));
    if (selected.length === 0) { setMessage({ type: 'err', text: 'Chưa chọn bệnh nhân nào.' }); return; }
    const actions = [`tạo phiếu${guiOnline ? ' + đẩy online' : ''}`, guiSms ? 'gửi SMS' : null].filter(Boolean).join(', ');
    if (!window.confirm(`Thực hiện ${actions} cho ${selected.length} bệnh nhân?`)) return;

    setBusy('process'); setMessage(null); setResult(null); setLogs([]);
    try {
      const res = await KskPrintService.process({
        maLuotKhams: selected.map(r => r.maLuotKham),
        guiOnline, guiSms,
        templateId: templateId.trim(),
        ngayIn: ngayIn ? ngayIn.split('-').reverse().join('/') : '',
      });
      setResult(res);
      setLogs(res.rows.flatMap(r => r.logs.map(l => `${r.maLuotKham} ${r.tenBenhNhan}: ${l}`)));
      // Cập nhật trạng thái trên lưới
      const byMa = Object.fromEntries(res.rows.map(r => [r.maLuotKham, r]));
      setRows(prev => prev.map(r => {
        const pr = byMa[r.maLuotKham];
        if (!pr) return r;
        return {
          ...r,
          daTaoFile: pr.fileStatus === 'ok' ? true : r.daTaoFile,
          daGuiSms: pr.smsStatus === 'ok' ? true : r.daGuiSms,
        };
      }));
      setMessage(res.failed > 0
        ? { type: 'warn', text: `Xong: ${res.fileOk} phiếu, ${res.smsOk} SMS, ${res.failed} lỗi — xem log bên dưới.` }
        : { type: 'ok', text: `Hoàn tất: ${res.fileOk} phiếu đã đẩy online, ${res.smsOk} SMS đã gửi.` });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.message || 'Xử lý thất bại.' });
    } finally { setBusy(''); }
  };

  const toggleAll = () => setChecked(p => p.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)));
  const toggleOne = (i) => setChecked(p => { const s = new Set(p); s.has(i) ? s.delete(i) : s.add(i); return s; });

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    borderRadius: 8, border: '1px solid ' + P.border, background: P.inputBg,
    fontSize: 14, fontFamily: font, color: P.text, outline: 'none',
  };
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 };
  const th = { padding: '9px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: P.textDim, whiteSpace: 'nowrap', borderBottom: '2px solid ' + P.border, position: 'sticky', top: 0, background: P.inputBg };
  const td = { padding: '8px 10px', fontSize: 13, color: P.text, borderBottom: '1px solid ' + P.border, whiteSpace: 'nowrap' };

  return (
    <div style={{ minHeight: '100vh', background: P.bg, fontFamily: font, color: P.text }}>
      <header className="app-header" style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: P.headerBg, borderBottom: '2px solid ' + P.primaryDark,
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(0,114,188,0.15)',
      }}>
        <button onClick={onBack} title="Quay lại" style={{
          width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)', color: P.headerText, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <div>
          <div className="header-title" style={{ fontSize: 15, fontWeight: 700, color: P.headerText }}>PHÒNG KHÁM ĐA KHOA KCN QUẾ VÕ</div>
          <div className="header-sub" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Trả kết quả KSK & gửi SMS</div>
        </div>
      </header>

      <div className="page-content" style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px 60px' }}>

        {/* ===== Bộ lọc ===== */}
        <Card style={{ padding: '18px 24px', marginBottom: 14 }}>
          <div className="import-step" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div ref={ctyRef} style={{ flex: '2 1 280px', position: 'relative' }}>
              <label style={lbl}>Công ty / doanh nghiệp</label>
              <input style={inputStyle} placeholder="Gõ tên hoặc mã..."
                value={searchCty}
                onChange={e => { setSearchCty(e.target.value); setOpenCty(true); if (company) { setCompany(null); setSoLo(''); } }}
                onFocus={() => setOpenCty(true)} />
              {openCty && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  background: P.white, border: '1px solid ' + P.border, borderRadius: 8,
                  marginTop: 4, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}>
                  {filteredCty.map(c => (
                    <div key={c.idKhachHang}
                      onClick={() => { setCompany(c); setSearchCty(c.tenKhachHang); setOpenCty(false); setSoLo(''); }}
                      style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid ' + P.border }}
                      onMouseEnter={e => e.currentTarget.style.background = P.primaryLight}
                      onMouseLeave={e => e.currentTarget.style.background = P.white}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{c.tenKhachHang}</div>
                      <div style={{ fontSize: 12, color: P.textMuted, fontFamily: mono }}>{c.maKhachHang}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 170px' }}>
              <label style={lbl}>Số lô</label>
              <select style={inputStyle} value={soLo} onChange={e => setSoLo(e.target.value)} disabled={!company}>
                <option value="">Tất cả lô</option>
                {soLoList.map(s => <option key={s.id} value={s.ma_solo}>{s.ma_solo} — {s.ten_solo}</option>)}
              </select>
            </div>
            <div style={{ flex: '0 1 150px' }}>
              <label style={lbl}>Từ ngày khám</label>
              <input type="date" style={inputStyle} value={tuNgay} onChange={e => setTuNgay(e.target.value)} />
            </div>
            <div style={{ flex: '0 1 150px' }}>
              <label style={lbl}>Đến ngày</label>
              <input type="date" style={inputStyle} value={denNgay} onChange={e => setDenNgay(e.target.value)} />
            </div>
            <button onClick={loadDanhSach} disabled={busy !== ''}
              style={btn(busy === '', P.primary)}>
              {busy === 'load' ? <Spinner size={16} color="#fff" /> : '🔍'} Tải danh sách
            </button>
          </div>
        </Card>

        {/* ===== Tùy chọn xử lý ===== */}
        <Card style={{ padding: '14px 24px', marginBottom: 14 }}>
          <div className="import-step" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={guiOnline} onChange={e => setGuiOnline(e.target.checked)} />
              Đẩy phiếu online (bệnh nhân tra cứu được)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={guiSms} onChange={e => setGuiSms(e.target.checked)} />
              Gửi SMS thông báo (kèm mật khẩu tra cứu)
            </label>
            {guiSms && (
              <input style={{ ...inputStyle, width: 200 }} placeholder="TEMPLATEID (trống = mặc định)"
                value={templateId} onChange={e => setTemplateId(e.target.value)} />
            )}
            <span style={{ fontSize: 13, color: P.textDim }}>Ngày in:</span>
            <input type="date" style={{ ...inputStyle, width: 150 }} value={ngayIn} onChange={e => setNgayIn(e.target.value)} />
            <button onClick={doProcess} disabled={busy !== '' || rows.length === 0}
              style={btn(rows.length > 0 && busy === '', P.success)}>
              {busy === 'process' ? <Spinner size={16} color="#fff" /> : '🚀'} Thực hiện ({checked.size})
            </button>
          </div>
        </Card>

        {message && (
          <div style={{
            padding: '12px 18px', borderRadius: 10, marginBottom: 14, fontSize: 14, fontWeight: 500,
            background: message.type === 'ok' ? '#e8f8ef' : message.type === 'warn' ? '#fdf3e7' : '#fde8e8',
            color: message.type === 'ok' ? P.success : message.type === 'warn' ? P.warning : P.danger,
            border: '1px solid ' + (message.type === 'ok' ? P.success : message.type === 'warn' ? P.warning : P.danger) + '40',
          }}>{message.text}</div>
        )}

        {/* ===== Danh sách ===== */}
        {rows.length > 0 && (
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
            <div style={{ overflow: 'auto', maxHeight: '52vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}><input type="checkbox" checked={checked.size === rows.length && rows.length > 0} onChange={toggleAll} /></th>
                    <th style={th}>Phiếu</th>
                    <th style={th}>SMS</th>
                    <th style={th}>Số hồ sơ</th>
                    <th style={th}>Mã NV</th>
                    <th style={{ ...th, minWidth: 160 }}>Họ tên</th>
                    <th style={th}>Ngày sinh</th>
                    <th style={th}>Giới tính</th>
                    <th style={th}>Bộ phận</th>
                    <th style={th}>SĐT</th>
                    <th style={th}>Số lô</th>
                    <th style={th}>Ngày khám</th>
                    <th style={th}>Mã lượt khám</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.maLuotKham}>
                      <td style={td}><input type="checkbox" checked={checked.has(i)} onChange={() => toggleOne(i)} /></td>
                      <td style={td}>{r.daTaoFile ? <Bdg bg="#e8f8ef" c={P.success} t="Đã tạo" /> : <Bdg bg="#fdf3e7" c={P.warning} t="Chưa" />}</td>
                      <td style={td}>{r.daGuiSms ? <Bdg bg="#e8f8ef" c={P.success} t="Đã gửi" /> : <Bdg bg="#eef2f7" c={P.textDim} t="Chưa" />}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.soHoSo}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.maNv}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{r.tenBenhNhan}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.ngaySinh}</td>
                      <td style={td}>{r.gioiTinh}</td>
                      <td style={td}>{r.boPhan}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.dienThoai}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.soLo}</td>
                      <td style={{ ...td, fontFamily: mono }}>{r.ngayKham}</td>
                      <td style={{ ...td, fontFamily: mono, color: P.primary }}>{r.maLuotKham}</td>
                      <td style={td}>
                        <button onClick={() => preview(r)} disabled={busy !== ''} title="Xem trước phiếu PDF"
                          style={{
                            padding: '5px 12px', borderRadius: 6, border: '1px solid ' + P.primary + '50',
                            background: P.white, color: P.primary, fontSize: 12, fontWeight: 600,
                            fontFamily: font, cursor: 'pointer',
                          }}>
                          {busy === 'preview:' + r.maLuotKham ? '...' : '👁 Xem'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* ===== Log ===== */}
        {logs.length > 0 && (
          <Card style={{ padding: '14px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: P.textDim, marginBottom: 8 }}>📜 Nhật ký xử lý</div>
            <div style={{ maxHeight: 220, overflowY: 'auto', fontFamily: mono, fontSize: 12, lineHeight: 1.8 }}>
              {logs.map((l, i) => (
                <div key={i} style={{ color: l.includes('thất bại') || l.includes('Lỗi') || l.includes('lỗi') ? P.danger : P.textDim }}>{l}</div>
              ))}
            </div>
          </Card>
        )}

        {rows.length === 0 && (
          <Card style={{ padding: '56px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📨</div>
            <p style={{ color: P.textDim, fontSize: 15, margin: 0 }}>
              Chọn công ty (hoặc số lô) rồi bấm "Tải danh sách" để bắt đầu trả kết quả.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function Bdg({ bg, c, t }) {
  return <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: c }}>{t}</span>;
}
const btn = (enabled, color) => ({
  padding: '10px 20px', borderRadius: 8, border: 'none',
  background: enabled ? color : '#b8cce0', color: '#fff',
  fontSize: 14, fontWeight: 600, fontFamily: font,
  cursor: enabled ? 'pointer' : 'not-allowed',
  display: 'flex', alignItems: 'center', gap: 8, height: 41,
});
